import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { Banner, Button, Card, Chip, IconButton, Input } from '@/components/ds-dlp';
import { AuthLayout } from '@/app/AuthLayout';
import { BRAND } from '@/brand';
import { useAuth } from '../contexts/AuthContext';
import { accountApi } from '../api/account';
import {
  detectTenantSlugFromHost,
  getPersistedTenantSlug,
  persistTenantSlug,
} from '../auth/tenantSlug';

// Login is the entry point, and the first screen on the d2d design system
// (cascade plan 3.5): ds Card, Input, Button and Banner in AuthLayout's frame.
//
// Sprint 4.3: explicit tenant slug. Two UX modes:
//   1. Subdomain detected (acme.example.com) — slug is locked, shown as a
//      chip; user only enters email + password.
//   2. Root domain (app.example.com, localhost) — slug is an editable
//      input. We pre-fill from the last successful login so a refresh
//      after token expiry doesn't re-prompt.
//
// "Forgot password?" shows only when the server can mail reset links.
const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Detect once. window.location.hostname doesn't change without a full
  // reload, so memoizing protects against accidental input churn.
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const subdomainSlug = useMemo(() => detectTenantSlugFromHost(hostname), [hostname]);
  const slugLocked = subdomainSlug !== null;

  const [tenantSlug, setTenantSlug] = useState<string>(subdomainSlug ?? getPersistedTenantSlug() ?? '');
  // The logo is the known tenant's: the subdomain's, or the last one signed in to.
  const [logoSlug] = useState<string | null>(() => subdomainSlug ?? getPersistedTenantSlug());
  const { data: resetByEmail } = useQuery({
    queryKey: ['password-reset-enabled'],
    queryFn: accountApi.passwordResetEnabled,
    staleTime: 5 * 60_000,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const slug = (slugLocked ? subdomainSlug! : tenantSlug).trim().toLowerCase();
    const result = await login(email, password, slug);
    if (!result.success) {
      setError(result.error || 'Login failed');
    } else if (!slugLocked) {
      // Remember the slug for next time on root-domain deploys. Subdomain
      // deploys carry it in the URL so we don't need localStorage there.
      persistTenantSlug(slug);
    }
    setIsLoading(false);
  };

  return (
    <AuthLayout subtitle="Sign in to your workspace" footer={`${BRAND.tagline} · v7.0`} tenantSlug={logoSlug}>
      <Card>
        <Card.Body>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate={false}>
            {error ? <Banner variant="danger">{error}</Banner> : null}

            {/* Tenant: a locked chip when the subdomain names it, an editable
                field on root-domain deploys. The chip avoids the "wait, am I on
                the right tenant?" doubt on subdomain logins. */}
            {slugLocked ? (
              <div className="flex flex-col gap-1.5">
                <span className="text-dense font-medium text-[var(--d2d-text-primary)]">Tenant</span>
                <div className="flex flex-wrap items-center gap-2">
                  <Chip variant="neutral">
                    <span className="font-mono">{subdomainSlug}</span>
                  </Chip>
                  <span className="text-micro text-[var(--d2d-text-muted)]">Detected from {hostname}</span>
                </div>
              </div>
            ) : (
              <Input
                id="login-tenant"
                label="Tenant"
                placeholder="acme"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                required
                autoComplete="organization"
                help="Your workspace identifier: lowercase letters, digits and underscores."
                className="font-mono"
              />
            )}

            <Input
              id="login-email"
              label="Email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus={slugLocked}
            />

            <Input
              id="login-password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              trailingSlot={
                <IconButton
                  size="sm"
                  icon={showPassword ? <EyeOff /> : <Eye />}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tooltip={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                />
              }
            />

            {resetByEmail ? (
              <div className="-mt-2 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="underline underline-offset-2"
                  onClick={() =>
                    navigate('/forgot-password', { state: { email, tenantSlug: slugLocked ? subdomainSlug : tenantSlug.trim().toLowerCase() } })
                  }
                >
                  Forgot password?
                </Button>
              </div>
            ) : null}

            <Button
              type="submit"
              loading={isLoading}
              disabled={isLoading || !email || !password || (!slugLocked && !tenantSlug.trim())}
              className="mt-1 w-full"
            >
              Sign in
            </Button>
          </form>
        </Card.Body>
      </Card>
    </AuthLayout>
  );
};

export default Login;
